import React from 'react';
import { FlaskConical, Printer } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

export const fmtDT = (v) => (v ? new Date(v).toLocaleString('en-IN', {
  timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
}) : '—');
export const money = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
export const validDays = (km) => Math.max(1, Math.ceil((Number(km) || 0) / 200));

/** Last time this bill's PDF was printed (single or Bulk Print) and how often. */
const fmtShort = (v) => new Date(v).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
export const PrintedMark = ({ b, testId }) => {
  if (!b.printed) return null;
  const { at, by_name: by, count } = b.printed;
  const more = count > 1 ? ` · ${count}×` : '';
  return (
    <span className="mt-0.5 block text-[11px] font-medium leading-tight text-sky-700" title={`Printed ${fmtDT(at)} by ${by || '—'}${more}`} data-testid={testId}>
      <span className="flex items-center gap-1 whitespace-nowrap"><Printer className="w-3 h-3 shrink-0" />Printed{more}</span>
      <span className="block whitespace-nowrap">{fmtShort(at)}</span>
      <span className="block max-w-[110px] truncate">{by || '—'}</span>
    </span>
  );
};

/** Bill went out with a Transporter ID and no vehicle: not valid for movement until the transporter enters Part B on the portal. */
export const PartBPending = ({ b, testId }) => (b.part_b_pending ? (
  <span className="mt-0.5 block whitespace-nowrap text-[11px] font-semibold text-amber-700" title="The transporter updates Part B (vehicle number) on the GST portal with their own login"
    data-testid={testId}>Part B pending — transporter to update</span>
) : null);

const MODE = {
  mock: ['MOCK', 'bg-amber-100 text-amber-800 hover:bg-amber-100'],
  sandbox: ['SANDBOX', 'bg-sky-100 text-sky-800 hover:bg-sky-100'],
  production: ['LIVE', 'bg-emerald-100 text-emerald-800 hover:bg-emerald-100'],
  tally: ['VIA TALLY', 'bg-teal-100 text-teal-800 hover:bg-teal-100'],
  portal: ['PORTAL JSON', 'bg-indigo-100 text-indigo-800 hover:bg-indigo-100'],
};
export const ModeBadge = ({ mode, testId }) => {
  const [label, cls] = MODE[mode] || [mode, 'bg-gray-100 text-gray-700 hover:bg-gray-100'];
  return <Badge className={cls} data-testid={testId}>{label}</Badge>;
};

const STATUS = {
  AWAITING: ['Waiting for e-way bill', 'bg-amber-100 text-amber-800 hover:bg-amber-100'],
  QUEUED: ['Waiting for Tally link', 'bg-slate-200 text-slate-700 hover:bg-slate-200'],
  IN_TALLY: ['In Tally — send e-way bill', 'bg-sky-100 text-sky-800 hover:bg-sky-100'],
  TALLY_ERROR: ['Tally error', 'bg-rose-100 text-rose-700 hover:bg-rose-100'],
  CANCELLED: ['Cancelled', 'bg-rose-100 text-rose-700 hover:bg-rose-100'],
  EXPIRED: ['Expired', 'bg-gray-200 text-gray-700 hover:bg-gray-200'],
  ACTIVE: ['Active', 'bg-emerald-100 text-emerald-800 hover:bg-emerald-100'],
  DISCARDED: ['Discarded', 'bg-gray-100 text-gray-600 hover:bg-gray-100'],
};
export const isDiscarded = (b) => b.status === 'CANCELLED' && b.cancel?.reason === 'Discarded in WMS';
export const StatusBadge = ({ b, testId }) => {
  const key = b.is_expired ? 'EXPIRED' : isDiscarded(b) ? 'DISCARDED' : b.status;
  const [label, cls] = STATUS[key] || [b.status, 'bg-gray-100 text-gray-700'];
  return <Badge className={`whitespace-nowrap ${cls}`} data-testid={testId}>{label}</Badge>;
};

export const ValidLeft = ({ b }) => {
  if (b.status !== 'ACTIVE' || b.is_expired) return null;
  const hrs = (new Date(b.valid_upto) - Date.now()) / 36e5;
  const txt = hrs < 24 ? `${Math.max(0, Math.floor(hrs))}h left` : `${Math.floor(hrs / 24)}d left`;
  return <span className={`ml-1 text-[11px] font-semibold ${hrs < 24 ? 'text-amber-600' : 'text-gray-500'}`}>{txt}</span>;
};

export const MockBanner = ({ cfg }) => {
  if (cfg?.mode !== 'mock') return null;
  return (
    <div className="flex items-start gap-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="ewb-mock-banner">
      <FlaskConical className="w-5 h-5 mt-0.5 shrink-0" />
      <p><b>MOCK e-way bills</b> — numbers here are fake test numbers, nothing is sent to the GST portal. Not valid for transport.</p>
    </div>
  );
};

export const PartyCard = ({ title, p, children, testId }) => (
  <div className="rounded-lg border border-gray-200 bg-white p-3 text-sm space-y-1" data-testid={testId}>
    <div className="flex items-center justify-between gap-2">
      <span className="text-[11px] uppercase tracking-wide text-gray-500">{title}</span>
      {children}
    </div>
    {p ? (
      <>
        <div className="font-semibold" data-testid={testId && `${testId}-name`}>{p.trade_name || p.legal_name}</div>
        {p.trade_name && p.trade_name !== p.legal_name && <div className="text-xs text-gray-500" data-testid={testId && `${testId}-legal`}>Legal name: {p.legal_name}</div>}
        <div className="font-mono text-xs">GSTIN {p.gstin}</div>
        <div className="text-gray-600">{[p.address1, p.address2].filter(Boolean).join(', ')}</div>
        {(p.place || p.pincode || p.state) && <div className="text-gray-600">{[[p.place, p.pincode].filter(Boolean).join(' – '), p.state].filter(Boolean).join(', ')}</div>}
      </>
    ) : <div className="text-rose-600">Not set</div>}
  </div>
);
